import React, { Component } from 'react';
import { connect } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import { Telemetry } from '@videoblocks/kafka-rest-client';
import { ChevronDown, ChevronUp } from '@videoblocks/react-icons';

import { StockItemArtistInterface } from '../../app/Search/containers/MenuContainerInterfaces';
import { ContentClass, ContentType } from '../../app/Shared/enums';
import FavoriteButton from '../../common/components/FavoriteButton';
import SignUpForMakerPopover from '../../common/components/Popover/SignUpForMakerPopover';
import { withUseMedia } from '../../common/hooks/useMedia';
import { StockItem, StockItemFormat } from '../../common/types/StockItemTypes';
import { someKeysDownHandler } from '../../common/utils';
import {
  selectDetailsAcquisitionOption,
  selectDetailsStockItem,
  selectDetailsStockItemArtists,
  selectDetailsStockItemIsContentClass,
} from '../selectors/detailsSelectors';
import StockItemContributor from './StockItemContributor';
import StockItemDownloadButton from './StockItemDownloadButton';
import StockItemTitle from './StockItemTitle';
import StockSpecs from './StockSpecs';

import './StockItemInfo.less';

interface Props {
  stockItem?: StockItem;
  stockItemArtists?: StockItemArtistInterface[];
  expanded?: boolean;
  selectedAcquisitionOption?: StockItemFormat;
  mediaQuery: boolean;
  isVideoDetailsPage?: boolean;
  isImageDetailsPage?: boolean;
}

class StockItemInfo extends Component<Props> {
  state = {
    readMore: false,
    showDetails: this.props.expanded || false,
  };

  toggleShowDetails() {
    this.setState({
      showDetails: !this.state.showDetails,
    });
    Telemetry.increment('stock.details.toggleMoreDetails');
  }

  render() {
    const { showDetails } = this.state;
    const { isVideoDetailsPage, isImageDetailsPage } = this.props;
    const isDetailsPageRedesign = isVideoDetailsPage || isImageDetailsPage;

    return (
      <div
        className="stockItemInfo flex flex-column md:mx-6"
        id="stock-item-info"
      >
        {!isDetailsPageRedesign && (
          <div className="stockItemInfoRow">
            <StockItemTitle />
          </div>
        )}
        <div className="stockItemInfoRow">{this.renderStockItemInfo()}</div>
        <div
          className={twMerge(
            isDetailsPageRedesign &&
              showDetails &&
              'border-gray-500 border-0 border-t border-solid py-0 md:py-2 mt-2 md:mt-4'
          )}
          tabIndex={showDetails ? 0 : null}
          aria-live={'polite'}
        >
          <div className="stockItemInfoRow overflow-hidden mx-4 mb-4">
            {showDetails && (
              <div className="stockItemInfoSection details mx-0">
                <div className="stockItemInfo-details">
                  <StockSpecs />
                </div>
              </div>
            )}
            <div className="stockItemInfoSection">
              <div className="stockItemInfo-actions">{this.props.children}</div>
            </div>
          </div>
          {showDetails && this.renderDescription()}
        </div>
      </div>
    );
  }

  renderStockItemInfo() {
    const { showDetails } = this.state;
    const { stockItem, mediaQuery, isVideoDetailsPage, isImageDetailsPage } =
      this.props;
    const isDetailsPageRedesign = isVideoDetailsPage || isImageDetailsPage;
    const toggleDetailsText = isVideoDetailsPage
      ? 'Video Details'
      : isImageDetailsPage
      ? 'Image Details'
      : `${showDetails ? 'Fewer' : 'More'} Details`;

    return (
      <>
        {!isDetailsPageRedesign && (
          <div className="stockItemInfoSection specs">
            <div className="stockItemInfo-stockSpec stockItemInfo-minimizedDetails">
              <StockItemContributor />
              {this.renderResolution()}
            </div>
          </div>
        )}
        <div
          className={twMerge(
            'stockItemInfoSection',
            isDetailsPageRedesign &&
              'mt-4 w-full flex justify-between flex-wrap sm:flex-nowrap gap-y-4 md:gap-y-0 md:justify-between'
          )}
        >
          <div
            tabIndex={0}
            role="button"
            aria-label={[toggleDetailsText, stockItem?.title].join(' - ')}
            aria-expanded={showDetails}
            data-cy="stockItemInfo-viewMore"
            className={twMerge(
              'min-w-28 md:min-w-38 text-xs uppercase cursor-pointer leading-20 flex items-center relative',
              isDetailsPageRedesign
                ? 'mb-2 md:mb-0 text-white text-sm normal-case font-semibold'
                : 'text-right'
            )}
            onClick={this.toggleShowDetails.bind(this)}
            onKeyDown={someKeysDownHandler(this.toggleShowDetails.bind(this))}
          >
            {toggleDetailsText}
            {showDetails ? (
              <ChevronUp className="w-2 h-2 mx-1.5 fill-current" />
            ) : (
              <ChevronDown className="w-2 h-2 mx-1.5 fill-current" />
            )}
          </div>
          {isDetailsPageRedesign && (
            <div className="flex justify-among gap-x-5">
              <div className="text-right min-w-30 md:min-w-38 text-sm text-white cursor-pointer leading-20 font-semibold">
                <StockItemDownloadButton
                  iconOnLeft={false}
                  previewText={
                    isVideoDetailsPage
                      ? 'Download Watermarked'
                      : 'Download Preview'
                  }
                />
              </div>
              <div className="min-w-30 order-first text-sm text-gray-900 cursor-pointer leading-20">
                <FavoriteButton
                  iconOnLeft={false}
                  stockItem={stockItem}
                  telemetryKey="details"
                  buttonText="Add to Folder"
                  buttonClasses="text-right text-white font-semibold"
                  iconClassName="w-4 h-4"
                  alternateIconDefaultColor="text-white"
                  containPoppers={true}
                />
              </div>
              {!mediaQuery &&
                stockItem.contentType !== ContentType.Template &&
                stockItem.contentType !== ContentType.Vr_360 && (
                  <div className="min-w-30 text-sm text-white cursor-pointer leading-20 font-semibold">
                    <SignUpForMakerPopover
                      mediaType={stockItem.contentClass}
                      stockItemId={stockItem.id}
                      className="justify-center py-0 h-full"
                      iconClassName="max-h-4 ml-2 fill-current text-white"
                      stayVisible
                      disableTooltip
                    >
                      Edit in Maker
                    </SignUpForMakerPopover>
                  </div>
                )}
            </div>
          )}
        </div>
      </>
    );
  }

  renderResolution(resolution = this.props.stockItem.maxResolution) {
    if (!resolution) {
      return null;
    }

    return (
      <div className="stockItemInfo-stockSpecResolution">
        <span className="stockItemInfo-stockSpecItemKey">Resolution: </span>
        <span className="stockItemInfo-stockSpecItemValue">{resolution}</span>
      </div>
    );
  }

  renderDescription() {
    const {
      stockItem: { description, isTemplate },
      isVideoDetailsPage,
    } = this.props;
    const { showDetails } = this.state;

    if (!showDetails || !description) return null;

    // previously, we only showed description if it existed & if the stockItem is an AE template
    if (!isVideoDetailsPage && !isTemplate) return null;

    return (
      <div
        className={twMerge(
          'stockItemInfoRow mx-4',
          isVideoDetailsPage && 'text-white'
        )}
      >
        <div className="stockItemInfo-descContainer">
          <p className={twMerge(isVideoDetailsPage && 'text-sm mb-6')}>
            <b>{isTemplate && 'Template '}Description: </b>
            {isVideoDetailsPage && <div className="my-4" />}
            {description}
          </p>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    stockItem: selectDetailsStockItem(state),
    stockItemArtists: selectDetailsStockItemArtists(state),
    selectedAcquisitionOption: selectDetailsAcquisitionOption(state),
    isVideoDetailsPage: selectDetailsStockItemIsContentClass(
      ContentClass.Video
    )(state),
    isImageDetailsPage: selectDetailsStockItemIsContentClass(
      ContentClass.Image
    )(state),
  };
}

const connectedComponent = connect(mapStateToProps)(StockItemInfo);

export default withUseMedia<Props>(connectedComponent, {
  query: '(max-width: 1199px)', // i.e. mobile
  defaultState: false,
});
